import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { todayISO } from "../lib/format";
import { PAYMENT_TYPES, PAYMENT_MODES, EXPENSE_PURPOSES } from "../lib/constants";
import { ReceiptDialog } from "../components/Receipt";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const ActionsContext = createContext(null);
export const useActions = () => useContext(ActionsContext);

const TITLES = {
  receive: "Receive Payment", expense: "Add Expense", advance_candidate: "Candidate Advance",
  pay_agency: "Pay Agency / Expense", give_advance: "Give Advance", refund: "Refund Candidate",
  transfer: "Transfer Money", add_candidate: "Add Candidate", recognize: "Recognize Income",
  adjust_advance: "Adjust Agency Advance", add_payee: "Add Payee", add_account: "Add Account",
};

export function ActionsProvider({ children }) {
  const [type, setType] = useState(null);
  const [preset, setPreset] = useState({});
  const [ref, setRef] = useState({ accounts: [], branches: [], incomeCats: [], expenseCats: [], candidates: [], payees: [] });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [onDoneCb, setOnDoneCb] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const openReceipt = useCallback((id) => setReceiptId(id), []);

  const loadRef = useCallback(async () => {
    try {
      const [acc, br, ic, ec, cand, pay] = await Promise.all([
        api.get("/accounts"), api.get("/branches"), api.get("/income-categories"),
        api.get("/expense-categories"), api.get("/candidates?limit=500"), api.get("/payees"),
      ]);
      setRef({
        accounts: acc.data, branches: br.data, incomeCats: ic.data, expenseCats: ec.data,
        candidates: cand.data.items, payees: pay.data.items,
      });
    } catch (e) { /* ignore */ }
  }, []);

  const open = useCallback((t, presetData = {}, onDone = null) => {
    setType(t);
    setPreset(presetData);
    setForm({ txn_date: todayISO(), payment_mode: "Cash", from_advance: true, ...presetData });
    setOnDoneCb(() => onDone);
    loadRef();
  }, [loadRef]);

  const close = () => { setType(null); setForm({}); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    const submittedType = type;
    try {
      let res;
      const f = form;
      if (type === "receive" || type === "advance_candidate") {
        const payload = { ...f, payment_type: type === "advance_candidate" ? "Candidate Advance" : f.payment_type };
        if (!payload.payment_type) throw { response: { data: { detail: "Select a payment type" } } };
        res = await api.post("/payments/receive", payload);
        toast.success(`${res.data.receipt_number} · Payment received`);
      } else if (type === "expense" || type === "pay_agency") {
        res = await api.post("/expenses", f);
        if (res.data?.overpay_advance > 0) toast.success(`Expense recorded · ₹${res.data.overpay_advance.toLocaleString("en-IN")} excess moved to advance`);
        else toast.success("Expense recorded");
      } else if (type === "give_advance") {
        res = await api.post("/advances/pay", f);
        toast.success("Advance paid");
      } else if (type === "adjust_advance") {
        res = await api.post("/advances/adjust", f);
        toast.success("Advance adjusted to expense");
      } else if (type === "recognize") {
        res = await api.post("/income/recognize", f);
        toast.success("Advance recognized as income");
      } else if (type === "transfer") {
        res = await api.post("/transfers", f);
        toast.success("Transfer completed");
      } else if (type === "refund") {
        res = await api.post("/refunds", f);
        toast.success("Refund recorded");
      } else if (type === "add_candidate") {
        res = await api.post("/candidates", f);
        toast.success(`${res.data.candidate_code} added`);
      } else if (type === "add_payee") {
        res = await api.post("/payees", f);
        toast.success("Payee added");
      } else if (type === "add_account") {
        res = await api.post("/accounts", f);
        toast.success("Account added");
      }
      close();
      if (res?.data?.status === "pending_approval") toast.info("Submitted for approval (above threshold)");
      if (onDoneCb) onDoneCb(res?.data);
      if (submittedType === "receive" && res?.data?.id) setReceiptId(res.data.id);
      window.dispatchEvent(new CustomEvent("prabhat:refresh"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActionsContext.Provider value={{ open, ref, openReceipt }}>
      {children}
      <ActionDialog {...{ type, form, set, ref, submit, saving, close, preset, reloadRef: loadRef }} />
      <ReceiptDialog id={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} />
    </ActionsContext.Provider>
  );
}

function AttachmentField({ onChange, onBusy }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true); onBusy?.(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const nf = [...files, { id: data.id, name: data.original_filename }];
      setFiles(nf); onChange(nf.map((x) => x.id));
      toast.success("Attached");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); onBusy?.(false); e.target.value = ""; }
  };
  const remove = (id) => { const nf = files.filter((x) => x.id !== id); setFiles(nf); onChange(nf.map((x) => x.id)); };
  return (
    <div>
      <label className="inline-flex items-center gap-2 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg px-3 py-2 cursor-pointer w-fit">
        {busy ? "Uploading..." : "Choose file"}
        <input type="file" className="hidden" onChange={handle} data-testid="field-attachment" accept="image/*,application/pdf" />
      </label>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {files.map((f) => (
          <span key={f.id} className="text-[11px] bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 flex items-center gap-1">
            {f.name.slice(0, 18)}
            <button type="button" onClick={() => remove(f.id)} className="text-teal-500 hover:text-rose-500">×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">{label}</Label>
      {children}
    </div>
  );
}

function Sel({ value, onChange, items, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {items.map((it) => (
          <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ActionDialog({ type, form, set, ref, submit, saving, close, reloadRef }) {
  const [showNp, setShowNp] = useState(false);
  const [npName, setNpName] = useState("");
  const addParty = async () => {
    if (!npName.trim()) return;
    try {
      const { data } = await api.post("/payees", { name: npName, type: "Agency" });
      if (reloadRef) await reloadRef();
      set("payee_id", data.id); setShowNp(false); setNpName(""); toast.success("Party created & saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!type) return null;
  const accItems = ref.accounts.map((a) => ({ value: a.id, label: `${a.name} (${a.type})` }));
  const brItems = ref.branches.map((b) => ({ value: b.id, label: b.name }));
  const candItems = ref.candidates.map((c) => ({ value: c.id, label: `${c.name} · ${c.candidate_code}` }));
  const payeeItems = ref.payees.map((p) => ({ value: p.id, label: `${p.name}${p.company_name ? " · " + p.company_name : ""}` }));
  const incItems = ref.incomeCats.map((c) => ({ value: c.id, label: c.name }));
  const expItems = ref.expenseCats.map((c) => ({ value: c.id, label: c.name }));
  const modeItems = PAYMENT_MODES.map((m) => ({ value: m, label: m }));

  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="action-dialog">
        <DialogHeader><DialogTitle className="font-heading">{TITLES[type]}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["receive", "advance_candidate", "refund", "recognize"].includes(type) && (
            <div className="sm:col-span-2"><F label="Candidate *">
              <Sel testid="field-candidate" value={form.candidate_id} onChange={(v) => set("candidate_id", v)} items={candItems} placeholder="Select candidate" />
            </F></div>
          )}
          {["give_advance", "adjust_advance", "pay_agency"].includes(type) && (
            <div className="sm:col-span-2"><F label={type === "pay_agency" ? "Payee (optional)" : "Payee / Agency *"}>
              <Sel testid="field-payee" value={form.payee_id} onChange={(v) => set("payee_id", v)} items={payeeItems} placeholder="Select payee" />
              <button type="button" onClick={() => setShowNp((s) => !s)} data-testid="new-party-toggle" className="text-xs text-teal-600 mt-1">+ New party</button>
              {showNp && (
                <div className="flex gap-2 mt-1">
                  <Input value={npName} onChange={(e) => setNpName(e.target.value)} placeholder="New party name" className="h-8" data-testid="new-party-name" />
                  <Button type="button" size="sm" onClick={addParty} data-testid="new-party-add">Add</Button>
                </div>
              )}
            </F></div>
          )}

          <F label="Date"><Input data-testid="field-date" type="date" value={form.txn_date || ""} onChange={(e) => set("txn_date", e.target.value)} className="h-9" /></F>

          {["receive"].includes(type) && (
            <F label="Payment Type *"><Sel testid="field-payment-type" value={form.payment_type} onChange={(v) => set("payment_type", v)} items={PAYMENT_TYPES.map((t) => ({ value: t, label: t }))} placeholder="Type" /></F>
          )}
          {(["receive", "expense", "advance_candidate", "give_advance", "refund", "transfer", "pay_agency", "adjust_advance", "recognize"].includes(type)) && (
            <F label="Amount (₹) *"><Input data-testid="field-amount" type="number" min="0" value={form.amount || ""} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} className="h-9 font-mono" placeholder="0.00" /></F>
          )}

          {["receive", "advance_candidate", "expense", "give_advance", "refund", "pay_agency"].includes(type) && (
            <>
              <F label="Payment Mode"><Sel testid="field-mode" value={form.payment_mode} onChange={(v) => set("payment_mode", v)} items={modeItems} placeholder="Mode" /></F>
              <F label={type === "refund" || type.includes("advance") || type === "give_advance" ? "Pay From Account *" : "Deposit Account *"}>
                <Sel testid="field-account" value={form.account_id} onChange={(v) => set("account_id", v)} items={accItems} placeholder="Account" />
              </F>
            </>
          )}

          {type === "transfer" && (
            <>
              <F label="From Account *"><Sel testid="field-from-account" value={form.from_account_id} onChange={(v) => set("from_account_id", v)} items={accItems} placeholder="From" /></F>
              <F label="To Account *"><Sel testid="field-to-account" value={form.to_account_id} onChange={(v) => set("to_account_id", v)} items={accItems} placeholder="To" /></F>
            </>
          )}

          {["expense", "pay_agency", "adjust_advance"].includes(type) && (
            <F label="Expense Category *"><Sel testid="field-expense-cat" value={form.expense_category_id} onChange={(v) => set("expense_category_id", v)} items={expItems} placeholder="Category" /></F>
          )}
          {["expense", "pay_agency"].includes(type) && (
            <>
              <div className="sm:col-span-2"><F label="For Candidate (optional)">
                <Sel testid="field-exp-candidate" value={form.candidate_id} onChange={(v) => set("candidate_id", v)} items={candItems} placeholder="Link this payment to a candidate" />
              </F></div>
              <div className="sm:col-span-2"><F label="Payment Purpose / Type">
                <Sel testid="field-exp-purpose" value={form.payment_type} onChange={(v) => set("payment_type", v)} items={EXPENSE_PURPOSES.map((p) => ({ value: p, label: p }))} placeholder="e.g. Visa Fees, Flight Ticket, Advance" />
              </F></div>
              <div className="sm:col-span-2 flex flex-wrap gap-4 text-xs text-slate-600 dark:text-slate-300">
                <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="field-on-credit" checked={!!form.on_credit} onChange={(e) => set("on_credit", e.target.checked)} className="h-4 w-4 accent-teal-600" /> Record as unpaid bill (payable, no cash out)</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="field-settle" checked={!!form.settle_payable} onChange={(e) => set("settle_payable", e.target.checked)} className="h-4 w-4 accent-teal-600" /> This payment settles a pending payable</label>
              </div>
              {form.on_credit && (() => {
                const sel = ref.payees.find((p) => p.id === form.payee_id);
                const advAvail = sel?.summary?.advance_pending || 0;
                if (advAvail <= 0) return null;
                return (
                  <div className="sm:col-span-2 border border-amber-200 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-900/10 rounded-lg p-2.5">
                    <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-200">
                      <input type="checkbox" data-testid="field-adjust-advance" checked={!!form.adjust_from_advance}
                        onChange={(e) => { set("adjust_from_advance", e.target.checked); if (e.target.checked) set("advance_adjust_amount", Math.min(advAvail, form.amount || advAvail)); }}
                        className="h-4 w-4 accent-teal-600" />
                      Adjust from existing advance (₹{advAvail.toLocaleString("en-IN")} available)
                    </label>
                    {form.adjust_from_advance && (
                      <Input data-testid="field-adjust-amount" type="number" min="0" value={form.advance_adjust_amount || ""}
                        onChange={(e) => set("advance_adjust_amount", parseFloat(e.target.value) || 0)}
                        className="h-8 mt-2 font-mono" placeholder="Amount to adjust from advance" />
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {type === "recognize" && (
            <F label="Income Category *"><Sel testid="field-income-cat" value={form.income_category_id} onChange={(v) => set("income_category_id", v)} items={incItems} placeholder="Category" /></F>
          )}

          {["receive", "expense", "advance_candidate", "give_advance", "transfer", "refund", "pay_agency", "adjust_advance", "recognize", "add_candidate"].includes(type) && (
            <F label="Branch"><Sel testid="field-branch" value={form.branch_id} onChange={(v) => set("branch_id", v)} items={brItems} placeholder="Branch" /></F>
          )}

          {type === "refund" && (
            <F label="Refund Source"><Sel testid="field-refund-source" value={form.from_advance ? "advance" : "income"} onChange={(v) => set("from_advance", v === "advance")} items={[{ value: "advance", label: "From Advance Held" }, { value: "income", label: "Refund of Recognized Income" }]} placeholder="Source" /></F>
          )}

          {type === "add_candidate" && (
            <>
              <div className="sm:col-span-2"><F label="Candidate Name *"><Input data-testid="field-cand-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" /></F></div>
              <F label="Mobile"><Input data-testid="field-cand-mobile" value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} className="h-9" /></F>
              <F label="Passport No."><Input value={form.passport_number || ""} onChange={(e) => set("passport_number", e.target.value)} className="h-9" /></F>
              <F label="Country Applied"><Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} className="h-9" /></F>
              <F label="Job / Trade"><Input value={form.trade || ""} onChange={(e) => set("trade", e.target.value)} className="h-9" /></F>
              <F label="Total Fee (₹)"><Input data-testid="field-cand-fee" type="number" value={form.total_fee || ""} onChange={(e) => set("total_fee", parseFloat(e.target.value) || 0)} className="h-9 font-mono" placeholder="Agreed fee to charge" /></F>
            </>
          )}
          {type === "add_payee" && (
            <>
              <div className="sm:col-span-2"><F label="Payee Name *"><Input data-testid="field-payee-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" /></F></div>
              <F label="Type"><Sel value={form.type || "Agency"} onChange={(v) => set("type", v)} items={["Agency", "Vendor", "Employee", "Staff", "Consultant", "Landlord", "Service Provider", "Other"].map((t) => ({ value: t, label: t }))} placeholder="Type" /></F>
              <F label="Mobile"><Input value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} className="h-9" /></F>
              <F label="Company"><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="h-9" /></F>
              <F label="Opening Balance"><Input type="number" value={form.opening_balance || ""} onChange={(e) => set("opening_balance", parseFloat(e.target.value) || 0)} className="h-9" /></F>
            </>
          )}
          {type === "add_account" && (
            <>
              <div className="sm:col-span-2"><F label="Account Name *"><Input data-testid="field-acc-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" /></F></div>
              <F label="Type"><Sel value={form.type || "Cash"} onChange={(v) => set("type", v)} items={["Cash", "Bank", "UPI", "Petty Cash", "Credit Card", "Other"].map((t) => ({ value: t, label: t }))} placeholder="Type" /></F>
              <F label="Opening Balance"><Input type="number" value={form.opening_balance || ""} onChange={(e) => set("opening_balance", parseFloat(e.target.value) || 0)} className="h-9" /></F>
              <F label="Bank Name"><Input value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className="h-9" /></F>
              <F label="Account No."><Input value={form.account_number || ""} onChange={(e) => set("account_number", e.target.value)} className="h-9" /></F>
            </>
          )}

          {["give_advance"].includes(type) && (
            <div className="sm:col-span-2"><F label="Purpose"><Input value={form.purpose || ""} onChange={(e) => set("purpose", e.target.value)} className="h-9" /></F></div>
          )}
          {["receive", "expense", "advance_candidate", "give_advance", "transfer", "refund", "pay_agency", "adjust_advance", "recognize"].includes(type) && (
            <>
              <div className="sm:col-span-2"><F label={type === "refund" ? "Refund Reason" : "Description"}><Input data-testid="field-description" value={form.description || form.reason || ""} onChange={(e) => set(type === "refund" ? "reason" : "description", e.target.value)} className="h-9" /></F></div>
              <div className="sm:col-span-2"><F label="Reference No. / Notes"><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="min-h-[60px]" /></F></div>
            </>
          )}
          {["receive", "expense", "advance_candidate", "give_advance", "refund", "pay_agency"].includes(type) && (
            <div className="sm:col-span-2"><F label="Attachment (screenshot / bill / proof)"><AttachmentField key={type} onChange={(ids) => set("attachment_ids", ids)} /></F></div>
          )}
          {type === "receive" && form.payment_type === "Other Income" && (
            <div className="sm:col-span-2"><F label="Custom Income Title"><Input value={form.custom_income_title || ""} onChange={(e) => set("custom_income_title", e.target.value)} className="h-9" /></F></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="action-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="action-submit" className="bg-[#0F2942] hover:bg-[#163a5c]">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
